import axios, { type AxiosError, type AxiosInstance } from 'axios';

import type { ProblemDetail } from '@/types/api';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:28000';

/**
 * Shared HTTP client.
 *
 * `withCredentials` is required by Sanctum's SPA cookie mode (M2): the session
 * lives in an httpOnly cookie, so no token is ever exposed to JavaScript. It
 * also means the API and SPA must be same-site — see SANCTUM_STATEFUL_DOMAINS.
 */
export const api: AxiosInstance = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  timeout: 15_000,
  // Laravel reads the CSRF token from the XSRF-TOKEN cookie and expects it
  // echoed in this header. Getting the names wrong yields a silent 419 on every
  // write.
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  // Matching names is NOT sufficient. Since Axios 1.6 the XSRF header is only
  // attached automatically for same-origin requests; cross-origin it requires
  // this opt-in, even with withCredentials set. The SPA runs on :25173 and the
  // API on :28000 — different origins — so without this the cookie is sent, the
  // header is not, and every state-changing request fails CSRF with a 419 that
  // looks like a server bug.
  withXSRFToken: true,
});

/**
 * Prime the CSRF cookie.
 *
 * Sanctum's SPA mode requires this before the first state-changing request of
 * a session. It hits /sanctum/csrf-cookie, which sits OUTSIDE the /api/v1
 * prefix, hence the absolute URL.
 */
export async function ensureCsrfCookie(): Promise<void> {
  await axios.get(`${BASE_URL}/sanctum/csrf-cookie`, { withCredentials: true });
}

/** A normalised error, so callers never have to unpack Axios internals. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly title: string,
    readonly detail?: string,
    readonly errors?: Record<string, string[]>,
  ) {
    super(detail ?? title);
    this.name = 'ApiError';
  }

  /** Field-level validation messages, if this was a 422. */
  get fieldErrors(): Record<string, string[]> {
    return this.errors ?? {};
  }

  /** True when no response arrived at all — the API was unreachable. */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ProblemDetail>) => {
    // No response at all: DNS failure, connection refused, timeout, or the
    // API container simply is not up yet.
    if (!error.response) {
      return Promise.reject(
        new ApiError(0, 'Network error', 'Could not reach the API. Is the stack running?'),
      );
    }

    const { status, data } = error.response;

    return Promise.reject(
      new ApiError(status, data?.title ?? error.message, data?.detail, data?.errors),
    );
  },
);
